import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';

function isMajorHeading(line) {
  const trimmed = line.trim();
  if (!trimmed || trimmed.length > 90) return false;
  if (!/[A-Z]/.test(trimmed)) return false;
  return trimmed === trimmed.toUpperCase();
}

function isMinorHeading(line) {
  const trimmed = line.trim();
  if (!trimmed || trimmed.length > 60) return false;
  if (trimmed.endsWith('.') || trimmed.includes(':')) return false;
  return /^[A-Za-z][A-Za-z\s&/-]*$/.test(trimmed);
}

function renderPolicyContent(policyContent) {
  const lines = policyContent.split(/\r?\n/);
  const elements = [];
  let listItems = [];
  let key = 0;

  const flushList = () => {
    if (!listItems.length) return;
    elements.push(
      <ul key={`ul-${key++}`} className="list-disc pl-5 space-y-1 text-slate-700">
        {listItems.map((item, idx) => (
          <li key={`${idx}-${item.slice(0, 20)}`}>{item}</li>
        ))}
      </ul>
    );
    listItems = [];
  };

  lines.forEach((lineRaw) => {
    const line = lineRaw.trimEnd();
    const compact = line.trim();

    if (!compact) {
      flushList();
      elements.push(<div key={`sp-${key++}`} className="h-1" />);
      return;
    }

    if (/^[\s\t]*[•-]\s+/.test(line)) {
      listItems.push(compact.replace(/^[•-]\s+/, ''));
      return;
    }

    flushList();

    if (isMajorHeading(compact)) {
      elements.push(
        <h2 key={`h2-${key++}`} className="text-base sm:text-lg font-semibold text-slate-900 mt-6 first:mt-0">
          {compact}
        </h2>
      );
      return;
    }

    if (isMinorHeading(compact)) {
      elements.push(
        <h3 key={`h3-${key++}`} className="text-sm font-semibold text-slate-800 mt-4">
          {compact}
        </h3>
      );
      return;
    }

    elements.push(
      <p key={`p-${key++}`} className="text-sm text-slate-700 leading-relaxed">
        {compact}
      </p>
    );
  });

  flushList();
  return elements;
}

export default function PrivacyPolicyPage() {
  const [policyContent, setPolicyContent] = useState('');

  useEffect(() => {
    let cancelled = false;

    const loadPolicy = async () => {
      try {
        const response = await fetch('/privacy-policy.txt');
        const text = await response.text();
        if (!cancelled) {
          setPolicyContent(text);
        }
      } catch {
        if (!cancelled) {
          setPolicyContent('');
        }
      }
    };

    loadPolicy();

    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-between">
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Desqly — Privacy Policy</h1>
          <Button asChild variant="outline">
            <Link to="/">Back to home</Link>
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-8">
        <Card id="privacy-policy">
          <CardHeader>
            <CardTitle>Privacy Policy</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {policyContent ? (
              <div className="rounded-lg border border-slate-200 bg-white p-4 sm:p-6">{renderPolicyContent(policyContent)}</div>
            ) : (
              <p className="text-sm text-slate-600">Loading privacy policy...</p>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
